import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * Item de database no formato dropdown (lista simplificada).
 *
 * Espelho à mão de `DatabaseDropdownItem` do backend
 * (`TROIA-V3-BACKEND/src/modules/databases/types.ts`) — ao mexer lá, mexer aqui.
 *
 * `status` e `hasIntegration` existem para MARCAR a base escolhida como DESTINO
 * em "copiar/mover registros": base inativa/arquivada e base que é espelho de um
 * feed externo aparecem na lista marcadas, nunca escondidas. O id da integração
 * não vem de propósito — a rota não tem permissão de módulo e a tela só precisa
 * saber SE a base é espelho de um feed.
 */
export interface DatabaseDropdownItem {
  id: string;
  name: string;
  type: string;
  /** 'active' | 'inactive' | 'archived' */
  status: string;
  /** `true` quando a base é espelho de um feed externo (ERP). */
  hasIntegration: boolean;
}

/**
 * Hook para buscar databases no formato dropdown
 * (id + name + type + status + hasIntegration).
 * Usa a rota /databases/dropdown (sem permissionMiddleware — cross-module).
 *
 * @param type Filtra por tipo de database (properties, vehicles, products, services, documents)
 * @param enabled Default `true`. Condicionar a `isOpen` em modais/drawers evita
 *   disparar a busca com o componente fechado (CLAUDE.md SEMPRE #10).
 *
 * @example
 * ```tsx
 * const { data: databases, isLoading } = useDatabasesDropdown('properties');
 * ```
 */
export function useDatabasesDropdown(type?: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ['databases', 'dropdown', type],
    queryFn: async () => {
      const response = await api.get(
        `databases/dropdown${type ? `?type=${encodeURIComponent(type)}` : ''}`
      );

      // Backend retorna { success: true, data: [...] }
      return response.data.data as DatabaseDropdownItem[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutos — databases mudam raramente
    refetchOnWindowFocus: false,
    enabled,
  });
}
